/**
 * The structure and contact sections of the MD analysis, after the
 * Schrödinger simulation interactions report: protein secondary structure over
 * the run, protein-ligand contacts by residue and over time, and the ligand's
 * torsion profiles. Pure builders on the md/1 packet (services md_spec): the
 * first three give ECharts options, the torsions an SVG string. The results
 * pane and the reports use the same builders.
 */
import { niceRange } from './mdCharts';
import { embeddedLigand, esc, parseResidueKey } from './mdInteractionDiagram';

const NO_DATA = (v) => v === null || v === undefined || Number.isNaN(Number(v));

function shade(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const mix = (c) => Math.round(c + (255 - c) * amount);
    return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => mix(c).toString(16).padStart(2, '0')).join('')}`;
}

const residueLabel = (key, resname) => {
    const { number } = parseResidueKey(key);
    return `${resname || ''}${number}`;
};

function byResidue(a, b) {
    const ka = parseResidueKey(a);
    const kb = parseResidueKey(b);
    if (ka.chain !== kb.chain) return ka.chain < kb.chain ? -1 : 1;
    return (parseInt(ka.number, 10) || 0) - (parseInt(kb.number, 10) || 0);
}

/**
 * Bar series drawing a category x frame map as run-length segments: for each
 * category, one stacked bar segment per run of equal characters in its string.
 * Far fewer elements than a heatmap cell per frame (a heatmap of tens of
 * thousands of cells also keeps painting after a report picture is taken).
 * @param {string[]} rows - one string per category, one character per frame
 * @param {Function} colorOf - `(char) => color` ('transparent' for a gap)
 * @param {object} axes - `{ xAxisIndex, yAxisIndex }`
 * @param {string} stack - the stack name
 * @returns {object[]} ECharts bar series
 */
export function runLengthSeries(rows, colorOf, axes, stack) {
    const runs = rows.map((row) => {
        const out = [];
        for (let i = 0; i < row.length; i += 1) {
            if (out.length && out[out.length - 1].c === row[i]) out[out.length - 1].n += 1;
            else out.push({ c: row[i], n: 1 });
        }
        return out;
    });
    const depth = Math.max(0, ...runs.map((r) => r.length));
    const series = [];
    for (let k = 0; k < depth; k += 1) {
        series.push({
            type: 'bar',
            stack,
            ...axes,
            barCategoryGap: '0%',
            animation: false,
            silent: true,
            data: runs.map((r) => (r[k]
                ? { value: r[k].n, itemStyle: { color: colorOf(r[k].c) } } : null)),
        });
    }
    return series;
}

// ---------------------------------------------------------------------------
// Secondary structure
// ---------------------------------------------------------------------------

export const SSE_COLORS = { helix: '#d0463b', strand: '#3b6fd0' };

export const SECONDARY_STRUCTURE_CAPTION = 'Protein secondary structure elements (SSE) during the '
    + 'run, assigned per frame by DSSP (cpptraj). Top: the fraction of the analysed frames each '
    + 'residue spends in a helix (alpha, 3-10 or pi; red) or a strand (extended or bridge; blue). '
    + 'Middle: each residue against time, colored by its assignment. Bottom: the percentage of '
    + 'residues in each element over time. Residues are numbered as in the submitted protein.';

/** The runs of a compound that have secondary structure data. */
export function secondaryStructureRuns(compound) {
    return (compound?.copies || []).filter((c) => (c.secondary_structure?.residues || []).length)
        .map((c) => c.copy);
}

/**
 * The secondary structure picture's data for a run choice: per-residue
 * fractions, the map (one run's) and the percentage series of the runs shown.
 * @param {object} compound
 * @param {'mean'|number} run
 */
export function secondaryStructureData(compound, run = 'mean') {
    const copies = (compound?.copies || []).filter((c) => c.secondary_structure?.residues?.length);
    if (copies.length === 0) return null;
    const shown = run === 'mean' ? copies : copies.filter((c) => c.copy === Number(run));
    if (shown.length === 0) return null;
    const perResidue = run === 'mean' && copies.length > 1 && compound.aggregate?.secondary_structure
        ? compound.aggregate.secondary_structure : shown[0].secondary_structure;
    const mapCopy = shown[0];
    const lines = shown.filter((c) => (c.series?.sse_helix_pct || []).length).map((c) => ({
        run: c.copy,
        time_ns: (c.series.time_ps || []).map((t) => Number(t) / 1000),
        helix: c.series.sse_helix_pct.map((v) => (NO_DATA(v) ? null : Number(v))),
        strand: (c.series.sse_strand_pct || []).map((v) => (NO_DATA(v) ? null : Number(v))),
    }));
    return {
        residues: perResidue.residues,
        labels: perResidue.residues.map((k, i) => residueLabel(k, (perResidue.resnames || [])[i])),
        helix: perResidue.helix,
        strand: perResidue.strand,
        mapRun: mapCopy.copy,
        map: mapCopy.secondary_structure.map || [],
        mapTimeNs: (mapCopy.secondary_structure.map_time_ps || []).map((t) => Number(t) / 1000),
        lines,
    };
}

/** Picture size (layout pixels) of the secondary structure chart. */
export function secondaryStructureSize(interactive = false) {
    return interactive ? { width: 1200, height: 520 } : { width: 1200, height: 900 };
}

/**
 * The ECharts option of the secondary structure chart: per-residue SSE
 * fractions, the residue x time map and the SSE percentages over time.
 * @param {object} compound
 * @param {object} [opts]
 * @param {'mean'|number} [opts.run]
 * @param {string} [opts.textColor]
 * @param {boolean} [opts.interactive]
 * @returns {object|null}
 */
export function secondaryStructureChartOption(compound, {
    run = 'mean', textColor = '#444', interactive = true,
} = {}) {
    const data = secondaryStructureData(compound, run);
    if (!data) return null;
    const fontSize = interactive ? 11 : 18;
    const axisText = { color: textColor, fontSize };
    const { height } = secondaryStructureSize(interactive);
    const left = interactive ? 64 : 110;
    const right = interactive ? 20 : 36;
    const top = interactive ? 22 : 40;
    const barsH = interactive ? 90 : 160;
    const gap = interactive ? 34 : 60;
    const linesH = interactive ? 90 : 160;
    const mapTop = top + barsH + gap;
    // Below the map: its residue labels and axis name, then the lines' title.
    const mapGap = interactive ? 62 : 110;
    const mapH = height - mapTop - mapGap - linesH - (interactive ? 36 : 64);
    const linesTop = mapTop + mapH + mapGap;
    const n = data.residues.length;
    const residueAxis = (gridIndex, show) => ({
        gridIndex,
        type: 'category',
        data: data.residues.map((k) => parseResidueKey(k).number),
        axisLabel: {
            ...axisText, show, interval: Math.max(0, Math.ceil(n / (interactive ? 16 : 14)) - 1),
        },
        axisTick: { show: false },
        name: show ? 'Residue' : '',
        nameLocation: 'middle',
        nameGap: interactive ? 24 : 40,
        nameTextStyle: axisText,
    });
    const pct = (v) => Math.round(Number(v) * 1000) / 10;
    const mapFrames = data.mapTimeNs.length;
    const t0 = data.mapTimeNs[0] || 0;
    const dt = mapFrames > 1 ? (data.mapTimeNs[mapFrames - 1] - t0) / (mapFrames - 1) : 0;
    const frameTime = (v) => String(Number((t0 + Number(v) * dt).toPrecision(3)));
    const sseColor = (c) => ({ H: SSE_COLORS.helix, E: SSE_COLORS.strand }[c] || 'transparent');
    const maxTime = niceRange(0, Math.max(0.001, ...data.lines.flatMap((l) => l.time_ns)))[1];
    const maxPct = niceRange(0, Math.max(1, ...data.lines.flatMap((l) => [...l.helix, ...l.strand]
        .filter((v) => v !== null))))[1];
    const series = [
        {
            name: 'Helix',
            type: 'bar',
            stack: 'sse',
            xAxisIndex: 0,
            yAxisIndex: 0,
            barCategoryGap: '10%',
            itemStyle: { color: SSE_COLORS.helix },
            data: data.helix.map(pct),
            animation: false,
        },
        {
            name: 'Strand',
            type: 'bar',
            stack: 'sse',
            xAxisIndex: 0,
            yAxisIndex: 0,
            barCategoryGap: '10%',
            itemStyle: { color: SSE_COLORS.strand },
            data: data.strand.map(pct),
            animation: false,
        },
        ...runLengthSeries(data.map, sseColor, { xAxisIndex: 1, yAxisIndex: 1 }, 'ssemap'),
    ];
    data.lines.forEach((l, k) => {
        series.push({
            name: `Helix, run ${l.run}`,
            type: 'line',
            xAxisIndex: 2,
            yAxisIndex: 2,
            showSymbol: false,
            animation: false,
            lineStyle: { width: interactive ? 1.2 : 2, color: shade(SSE_COLORS.helix, 0.3 * k) },
            itemStyle: { color: shade(SSE_COLORS.helix, 0.3 * k) },
            data: l.time_ns.map((t, j) => [t, l.helix[j]]),
        });
        series.push({
            name: `Strand, run ${l.run}`,
            type: 'line',
            xAxisIndex: 2,
            yAxisIndex: 2,
            showSymbol: false,
            animation: false,
            lineStyle: { width: interactive ? 1.2 : 2, color: shade(SSE_COLORS.strand, 0.3 * k) },
            itemStyle: { color: shade(SSE_COLORS.strand, 0.3 * k) },
            data: l.time_ns.map((t, j) => [t, l.strand[j]]),
        });
    });
    return {
        animation: false,
        textStyle: axisText,
        legend: {
            data: ['Helix', 'Strand'],
            top: 0,
            right,
            textStyle: axisText,
            itemWidth: interactive ? 14 : 22,
            itemHeight: interactive ? 10 : 16,
        },
        grid: [
            {
                left, right, top, height: barsH,
            },
            {
                left, right, top: mapTop, height: mapH,
            },
            {
                left, right, top: linesTop, height: linesH,
            },
        ],
        xAxis: [
            residueAxis(0, false),
            residueAxis(1, true),
            {
                gridIndex: 2,
                type: 'value',
                min: 0,
                max: maxTime,
                name: 'Time (ns)',
                nameLocation: 'middle',
                nameGap: interactive ? 24 : 40,
                nameTextStyle: axisText,
                axisLabel: axisText,
                splitLine: { show: false },
            },
        ],
        yAxis: [
            {
                gridIndex: 0,
                type: 'value',
                min: 0,
                max: 100,
                name: '% SSE',
                nameTextStyle: { ...axisText, align: 'left' },
                axisLabel: axisText,
                splitLine: { lineStyle: { opacity: 0.12 } },
            },
            {
                gridIndex: 1,
                type: 'value',
                min: 0,
                max: mapFrames,
                name: `Time (ns), run ${data.mapRun}`,
                nameTextStyle: { ...axisText, align: 'left' },
                axisLabel: { ...axisText, formatter: frameTime },
                splitLine: { show: false },
            },
            {
                gridIndex: 2,
                type: 'value',
                min: 0,
                max: maxPct,
                name: '% SSE',
                nameTextStyle: { ...axisText, align: 'left' },
                axisLabel: axisText,
                splitLine: { lineStyle: { opacity: 0.12 } },
            },
        ],
        tooltip: interactive ? { trigger: 'item' } : { show: false },
        series,
    };
}

// ---------------------------------------------------------------------------
// Protein-ligand contacts by residue
// ---------------------------------------------------------------------------

/** Contact types in stacking order, with the report's colors. */
export const CONTACT_TYPES = [
    { key: 'hbond', label: 'H-bonds', color: '#3a9d4f' },
    { key: 'hydrophobic', label: 'Hydrophobic', color: '#8e6bbf' },
    { key: 'pi_pi', label: 'π–π', color: '#e08a2c' },
    { key: 'pi_cation', label: 'π–cation', color: '#b5553a' },
    { key: 'ionic', label: 'Ionic', color: '#d1477a' },
    { key: 'water_bridge', label: 'Water bridges', color: '#4a8fd0' },
];

export const CONTACTS_HISTOGRAM_CAPTION = 'Protein-ligand contacts by residue, by type: for each '
    + 'residue, the fraction of the run each kind of interaction is present, summed over the '
    + 'ligand atoms and groups that make it, so a value above 1 means the residue makes more '
    + 'than one interaction of that type at once. Types are defined as in the interaction '
    + 'diagram (H-bonds from cpptraj over every analysed frame; the others on up to 500 evenly '
    + 'spaced frames).';

function rowsFor(compound, run) {
    if (run !== 'mean') {
        const copy = (compound?.copies || []).find((c) => c.copy === Number(run));
        return copy?.ligand_interactions?.rows || [];
    }
    const agg = compound?.aggregate?.ligand_interactions?.rows;
    if (agg && agg.length) return agg;
    const only = (compound?.copies || []).find((c) => (c.ligand_interactions?.rows || []).length);
    return only?.ligand_interactions?.rows || [];
}

/** The runs of a compound with interaction rows. */
export function contactRuns(compound) {
    return (compound?.copies || []).filter((c) => (c.ligand_interactions?.rows || []).length)
        .map((c) => c.copy);
}

/**
 * Per residue (in sequence order), the summed occupancy of each contact type.
 * @param {object} compound
 * @param {'mean'|number} run
 * @returns {{ residue: string, label: string, values: Object<string, number> }[]}
 */
export function contactHistogramRows(compound, run = 'mean') {
    const byRes = new Map();
    for (const r of rowsFor(compound, run)) {
        if (!byRes.has(r.residue)) {
            byRes.set(r.residue, {
                residue: r.residue, label: residueLabel(r.residue, r.resname), values: {},
            });
        }
        const entry = byRes.get(r.residue);
        entry.values[r.type] = (entry.values[r.type] || 0) + Number(r.occupancy || 0);
    }
    return [...byRes.values()].sort((a, b) => byResidue(a.residue, b.residue));
}

/**
 * The ECharts option of the contacts histogram (stacked bars per residue).
 * @returns {object|null}
 */
export function contactHistogramChartOption(compound, {
    run = 'mean', textColor = '#444', interactive = true,
} = {}) {
    const rows = contactHistogramRows(compound, run);
    if (rows.length === 0) return null;
    const fontSize = interactive ? 11 : 18;
    const axisText = { color: textColor, fontSize };
    const types = CONTACT_TYPES.filter((t) => rows.some((r) => r.values[t.key]));
    return {
        animation: false,
        textStyle: axisText,
        legend: {
            data: types.map((t) => t.label),
            top: 0,
            textStyle: axisText,
            itemWidth: interactive ? 14 : 22,
            itemHeight: interactive ? 10 : 16,
        },
        grid: {
            left: interactive ? 56 : 90,
            right: interactive ? 20 : 36,
            top: interactive ? 34 : 60,
            bottom: interactive ? 64 : 110,
        },
        tooltip: interactive ? { trigger: 'axis', axisPointer: { type: 'shadow' } } : { show: false },
        xAxis: {
            type: 'category',
            data: rows.map((r) => r.label),
            axisLabel: { ...axisText, rotate: 60, interval: 0 },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            min: 0,
            name: 'Interactions fraction',
            nameTextStyle: { ...axisText, align: 'left' },
            axisLabel: axisText,
            splitLine: { lineStyle: { opacity: 0.12 } },
        },
        series: types.map((t) => ({
            name: t.label,
            type: 'bar',
            stack: 'contacts',
            barCategoryGap: '25%',
            animation: false,
            itemStyle: { color: t.color },
            data: rows.map((r) => Math.round((r.values[t.key] || 0) * 1000) / 1000),
        })),
    };
}

// ---------------------------------------------------------------------------
// Contacts over time
// ---------------------------------------------------------------------------

export const CONTACTS_TIMELINE_CAPTION = 'Protein-ligand contacts over time. Top: the total '
    + 'number of interactions of every type in each evaluated frame. Bottom: the residues that '
    + 'interact with the ligand; a cell is shaded in the frames where the residue makes at least '
    + 'one interaction, darker when it makes several at once (up to 9).';

/** The runs of a compound with a contacts timeline. */
export function contactTimelineRuns(compound) {
    return (compound?.copies || [])
        .filter((c) => (c.ligand_interactions?.timeline?.time_ps || []).length
            && (c.ligand_interactions.timeline.residues || []).length)
        .map((c) => c.copy);
}

/** Picture size (layout pixels) of the timeline for `residues` rows. */
export function contactTimelineSize(residues, interactive = false) {
    const row = interactive ? 14 : 24;
    return { width: 1200, height: (interactive ? 190 : 330) + Math.max(4, residues) * row };
}

/** The residue count of the timeline drawn for `run` (default the first run). */
export function contactTimelineResidueCount(compound, run) {
    const runs = contactTimelineRuns(compound);
    const pick = run === undefined || run === 'first' ? runs[0] : Number(run);
    const copy = (compound?.copies || []).find((c) => c.copy === pick);
    return copy?.ligand_interactions?.timeline?.residues?.length || 0;
}

const TIMELINE_SHADES = ['#fde0c5', '#f9b98a', '#f08a4b', '#d9622b', '#a8401b'];

/**
 * The ECharts option of one run's contacts timeline.
 * @param {object} compound
 * @param {object} [opts]
 * @param {'first'|number} [opts.run] - default the first run with a timeline
 * @returns {object|null}
 */
export function contactTimelineChartOption(compound, {
    run = 'first', textColor = '#444', interactive = true,
} = {}) {
    const runs = contactTimelineRuns(compound);
    const pick = run === 'first' ? runs[0] : Number(run);
    const copy = (compound?.copies || []).find((c) => c.copy === pick);
    const tl = copy?.ligand_interactions?.timeline;
    if (!tl || !(tl.time_ps || []).length || !(tl.residues || []).length) return null;
    const fontSize = interactive ? 11 : 18;
    const axisText = { color: textColor, fontSize };
    const { height } = contactTimelineSize(tl.residues.length, interactive);
    const left = interactive ? 72 : 120;
    const right = interactive ? 20 : 36;
    const top = interactive ? 24 : 40;
    const totalH = interactive ? 80 : 140;
    const gap = interactive ? 30 : 54;
    const mapTop = top + totalH + gap;
    const mapH = height - mapTop - (interactive ? 40 : 70);
    const times = tl.time_ps.map((t) => String(Number((Number(t) / 1000).toPrecision(4))));
    const totals = tl.time_ps.map((_t, i) => Object.values(tl.totals || {})
        .reduce((s, v) => s + (Number(v[i]) || 0), 0));
    const labels = tl.residues.map((r) => residueLabel(r.residue, r.resname));
    const n = times.length;
    const t0 = Number(tl.time_ps[0]) / 1000;
    const dt = n > 1 ? (Number(tl.time_ps[n - 1]) / 1000 - t0) / (n - 1) : 0;
    // Frame i spans [i, i + 1] on the map's axis and is category i on the totals' axis.
    const frameTime = (v) => String(Number((t0 + Number(v) * dt).toPrecision(3)));
    const shadeOf = (c) => (c === '0' ? 'transparent'
        : TIMELINE_SHADES[Math.min(TIMELINE_SHADES.length, Number(c)) - 1]);
    return {
        animation: false,
        textStyle: axisText,
        grid: [
            {
                left, right, top, height: totalH,
            },
            {
                left, right, top: mapTop, height: mapH,
            },
        ],
        xAxis: [
            {
                gridIndex: 0, type: 'category', data: times, axisLabel: { show: false }, axisTick: { show: false },
            },
            {
                gridIndex: 1,
                type: 'value',
                min: 0,
                max: n,
                axisLabel: { ...axisText, formatter: frameTime },
                splitLine: { show: false },
                name: 'Time (ns)',
                nameLocation: 'middle',
                nameGap: interactive ? 24 : 40,
                nameTextStyle: axisText,
            },
        ],
        yAxis: [
            {
                gridIndex: 0,
                type: 'value',
                min: 0,
                name: 'Contacts',
                nameTextStyle: { ...axisText, align: 'left' },
                axisLabel: axisText,
                splitLine: { lineStyle: { opacity: 0.12 } },
                minInterval: 1,
            },
            {
                gridIndex: 1,
                type: 'category',
                data: labels,
                inverse: true,
                axisLabel: { ...axisText, interval: 0 },
                axisTick: { show: false },
            },
        ],
        tooltip: interactive ? { trigger: 'item' } : { show: false },
        series: [
            {
                name: 'Contacts',
                type: 'bar',
                xAxisIndex: 0,
                yAxisIndex: 0,
                barCategoryGap: '0%',
                animation: false,
                itemStyle: { color: '#a8401b' },
                data: totals,
            },
            ...runLengthSeries(tl.residues.map((r) => r.counts), shadeOf,
                { xAxisIndex: 1, yAxisIndex: 1 }, 'timeline'),
        ],
    };
}

// ---------------------------------------------------------------------------
// Ligand torsions (an SVG: the ligand with its rotatable bonds, and a dial and
// a histogram per torsion)
// ---------------------------------------------------------------------------

export const TORSION_COLORS = ['#d1477a', '#3a9d4f', '#4a8fd0', '#e08a2c', '#8e6bbf', '#b5553a',
    '#2aa198', '#c9a227', '#6c71c4', '#859900', '#cb4b16', '#268bd2'];

export const LIGAND_TORSIONS_CAPTION = 'Ligand torsion profiles: each rotatable bond (single, '
    + 'not in a ring, between heavy atoms that each have another heavy neighbour) is colored on '
    + 'the ligand and numbered. For each, the dial shows the dihedral angle over time (the run '
    + 'starts at the centre and ends at the edge; 0° at the top) and the histogram the fraction '
    + 'of all analysed frames in each 10° bin from -180° to 180°. Atoms are numbered as in the '
    + 'submitted molecule.';

/** The runs of a compound with torsion profiles. */
export function torsionRuns(compound) {
    return (compound?.copies || []).filter((c) => (c.ligand_torsions || []).length)
        .map((c) => c.copy);
}

/** True when the compound has torsions and a ligand drawing to show them on. */
export function hasLigandTorsions(compound) {
    return torsionRuns(compound).length > 0 && !!compound?.ligand_2d?.svg;
}

/**
 * The torsions to draw for a run choice: atoms, dial points per run and the histogram.
 * @param {object} compound
 * @param {'all'|number} run
 */
export function torsionData(compound, run = 'all') {
    const copies = (compound?.copies || []).filter((c) => (c.ligand_torsions || []).length
        && (run === 'all' || c.copy === Number(run)));
    if (copies.length === 0) return [];
    const agg = run === 'all' && copies.length > 1 ? compound.aggregate?.ligand_torsions || [] : [];
    const keyOf = (atoms) => atoms.join('-');
    const order = [];
    const byKey = new Map();
    for (const c of copies) {
        const times = c.series?.time_ps || [];
        for (const t of c.ligand_torsions) {
            const key = keyOf(t.atoms);
            if (!byKey.has(key)) {
                byKey.set(key, { atoms: t.atoms, runs: [], histogram: t.histogram });
                order.push(key);
            }
            byKey.get(key).runs.push({ run: c.copy, angles: t.series || [], times });
        }
    }
    for (const a of agg) {
        if (byKey.has(keyOf(a.atoms))) byKey.get(keyOf(a.atoms)).histogram = a.histogram;
    }
    return order.map((k) => byKey.get(k));
}

const TORSION_PANEL = { width: 360, height: 150 };
const DRAWING_BOX = 380;

/** Picture size (layout pixels) of the torsion profiles for `count` torsions. */
export function ligandTorsionsSize(count) {
    const rows = Math.ceil(Math.max(1, count) / 2);
    return {
        width: DRAWING_BOX + 2 * TORSION_PANEL.width + 60,
        height: Math.max(DRAWING_BOX + 40, rows * TORSION_PANEL.height + 50),
    };
}

const FONT = "font-family=\"'Helvetica Neue', Helvetica, Arial, sans-serif\"";
const r1 = (v) => Math.round(v * 10) / 10;

function torsionPanel(t, k, x0, y0) {
    const color = TORSION_COLORS[k % TORSION_COLORS.length];
    const parts = [];
    parts.push(`<text x="${x0}" y="${y0 + 14}" font-size="13" font-weight="bold" fill="${color}" ${FONT}>`
        + `${k + 1}</text>`);
    parts.push(`<text x="${x0 + 18}" y="${y0 + 14}" font-size="11.5" fill="#444" ${FONT}>`
        + `atoms ${esc(t.atoms.join('–'))}</text>`);
    // Dial: radius = time, angle = dihedral (0 at the top, clockwise positive).
    const cx = x0 + 62;
    const cy = y0 + 84;
    const R = 52;
    parts.push(`<circle cx="${cx}" cy="${cy}" r="${R}" fill="none" stroke="#c8c8c8" stroke-width="1"/>`);
    parts.push(`<circle cx="${cx}" cy="${cy}" r="${R / 2}" fill="none" stroke="#e2e2e2" stroke-width="1"/>`);
    parts.push(`<line x1="${cx}" y1="${cy - R}" x2="${cx}" y2="${cy + R}" stroke="#e2e2e2"/>`);
    parts.push(`<line x1="${cx - R}" y1="${cy}" x2="${cx + R}" y2="${cy}" stroke="#e2e2e2"/>`);
    parts.push(`<text x="${cx}" y="${cy - R - 3}" text-anchor="middle" font-size="9.5" fill="#777" ${FONT}>0°</text>`);
    t.runs.forEach((r, j) => {
        const n = r.angles.length;
        const dots = r.angles.map((a, i) => {
            if (NO_DATA(a)) return '';
            const rad = (Number(a) * Math.PI) / 180;
            const rr = R * (0.12 + 0.88 * (n > 1 ? i / (n - 1) : 1));
            return `<circle cx="${r1(cx + rr * Math.sin(rad))}" cy="${r1(cy - rr * Math.cos(rad))}" r="1.6"/>`;
        }).join('');
        parts.push(`<g fill="${shade(color, 0.3 * j)}" fill-opacity="0.75">${dots}</g>`);
    });
    // Histogram: 36 bins, -180..180.
    const hx = x0 + 132;
    const hy = y0 + 32;
    const hw = 210;
    const hh = 92;
    const hist = t.histogram || [];
    const max = Math.max(0.0001, ...hist);
    const bw = hw / Math.max(1, hist.length);
    parts.push(`<line x1="${hx}" y1="${hy + hh}" x2="${hx + hw}" y2="${hy + hh}" stroke="#999"/>`);
    parts.push(`<g fill="${color}">${hist.map((v, i) => {
        const h = (hh * v) / max;
        return h > 0 ? `<rect x="${r1(hx + i * bw + 0.5)}" y="${r1(hy + hh - h)}" width="${r1(bw - 1)}" height="${r1(h)}"/>` : '';
    }).join('')}</g>`);
    for (const [v, label] of [[0, '-180'], [0.5, '0'], [1, '180']]) {
        parts.push(`<text x="${r1(hx + v * hw)}" y="${hy + hh + 13}" text-anchor="middle" font-size="9.5" `
            + `fill="#777" ${FONT}>${label}</text>`);
    }
    parts.push(`<text x="${hx}" y="${hy - 4}" font-size="9.5" fill="#777" ${FONT}>max ${Math.round(max * 100)}%</text>`);
    return parts.join('');
}

/**
 * The torsion profiles as one standalone SVG document string.
 * @param {object} compound
 * @param {object} [opts]
 * @param {'all'|number} [opts.run]
 * @returns {string} '' when there are no torsions or no ligand drawing
 */
export function ligandTorsionsSvg(compound, { run = 'all' } = {}) {
    const torsions = torsionData(compound, run);
    const ligand = compound?.ligand_2d;
    if (torsions.length === 0 || !ligand?.svg) return '';
    const { width, height } = ligandTorsionsSize(torsions.length);
    const scale = Math.min(DRAWING_BOX / ligand.width, DRAWING_BOX / ligand.height);
    const ox = 20 + (DRAWING_BOX - ligand.width * scale) / 2;
    const oy = 20 + (DRAWING_BOX - ligand.height * scale) / 2;
    const at = (num) => {
        const a = ligand.atoms?.[String(num)];
        return a ? { x: ox + a.x * scale, y: oy + a.y * scale } : null;
    };
    const overlays = torsions.map((t, k) => {
        const p = at(t.atoms[1]);
        const q = at(t.atoms[2]);
        if (!p || !q) return '';
        const color = TORSION_COLORS[k % TORSION_COLORS.length];
        const mx = (p.x + q.x) / 2;
        const my = (p.y + q.y) / 2;
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const d = Math.hypot(dx, dy) || 1;
        const lx = mx - (dy / d) * 13;
        const ly = my + (dx / d) * 13;
        return `<line x1="${r1(p.x)}" y1="${r1(p.y)}" x2="${r1(q.x)}" y2="${r1(q.y)}" stroke="${color}" `
            + 'stroke-width="9" stroke-linecap="round" stroke-opacity="0.45"/>'
            + `<circle cx="${r1(lx)}" cy="${r1(ly)}" r="8.5" fill="#fff" stroke="${color}" stroke-width="1.5"/>`
            + `<text x="${r1(lx)}" y="${r1(ly + 4)}" text-anchor="middle" font-size="11" font-weight="bold" `
            + `fill="${color}" ${FONT}>${k + 1}</text>`;
    }).join('');
    const panels = torsions.map((t, k) => torsionPanel(
        t, k,
        DRAWING_BOX + 40 + (k % 2) * TORSION_PANEL.width,
        20 + Math.floor(k / 2) * TORSION_PANEL.height,
    )).join('');
    const runNote = run === 'all' ? '' : `<text x="20" y="${DRAWING_BOX + 34}" font-size="12" fill="#555" ${FONT}>Run ${esc(run)}</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" `
        + `viewBox="0 0 ${width} ${height}" role="img" aria-label="Ligand torsion profiles">`
        + `<rect x="0" y="0" width="${width}" height="${height}" rx="10" fill="#ffffff" stroke="#d0d0d0"/>`
        + `${embeddedLigand({
            ...ligand, x: ox, y: oy, scale,
        })}${overlays}${panels}${runNote}</svg>`;
}

/** The report's torsion picture (all runs) and its size. */
export function ligandTorsionsReportSvg(compound) {
    return ligandTorsionsSvg(compound, { run: 'all' });
}

export function ligandTorsionsReportSize(compound) {
    return ligandTorsionsSize(torsionData(compound, 'all').length);
}
